/**
 * Weather File Map Component
 *
 * Interactive map showing weather station locations with clustering.
 * Uses Leaflet with react-leaflet for the map rendering.
 */

import { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import type { WeatherFileMetadata, WeatherFileSearchResult, WeatherFileSearchQuery } from '../../types';
import { searchWeatherFiles } from '../../services/weatherFileSearch';

// Fix Leaflet default marker icon issue
// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default.prototype as any)['_getIconUrl'];
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon for weather stations
const weatherStationIcon = L.divIcon({
  className: 'weather-station-marker',
  html: `<div class="weather-marker-inner"></div>`,
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});

// Selected marker icon
const selectedStationIcon = L.divIcon({
  className: 'weather-station-marker selected',
  html: `<div class="weather-marker-inner selected"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

export interface WeatherFileMapProps {
  onSelect?: (file: WeatherFileMetadata) => void;
  onPreview?: (file: WeatherFileMetadata) => void;
  selectedFile?: WeatherFileMetadata | null;
  initialCenter?: [number, number];
  initialZoom?: number;
  /** Search query/filters to apply to map results */
  searchQuery?: WeatherFileSearchQuery;
}

// Component to handle map events and marker clustering
function MapContent({
  onSelect,
  onPreview,
  selectedFile,
  searchQuery,
}: {
  onSelect?: ((file: WeatherFileMetadata) => void) | undefined;
  onPreview?: ((file: WeatherFileMetadata) => void) | undefined;
  selectedFile?: WeatherFileMetadata | null | undefined;
  searchQuery?: WeatherFileSearchQuery | undefined;
}) {
  const map = useMap();
  const markersRef = useRef<L.MarkerClusterGroup | null>(null);
  const [files, setFiles] = useState<WeatherFileSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadingTimeoutRef = useRef<number | null>(null);
  const currentBoundsRef = useRef<string | null>(null);
  const isMapReady = useRef(false);

  // Serialize searchQuery for comparison
  const searchQueryKey = useMemo(() => {
    if (!searchQuery) return '';
    return JSON.stringify({
      text: searchQuery.text,
      countryCode: searchQuery.countryCode,
      climateZone: searchQuery.climateZone,
      sourceType: searchQuery.sourceType,
      yearRange: searchQuery.yearRange,
    });
  }, [searchQuery]);

  // Debounced load function
  const loadFilesForBounds = useCallback(async () => {
    if (!isMapReady.current) return;

    const bounds = map.getBounds();
    // Validate bounds
    if (!bounds || !bounds.isValid()) {
      console.warn('Invalid map bounds, skipping load');
      return;
    }

    const boundsKey = `${bounds.getNorth().toFixed(3)},${bounds.getSouth().toFixed(3)},${bounds.getEast().toFixed(3)},${bounds.getWest().toFixed(3)}-${searchQueryKey}`;

    // Skip if bounds and filters haven't changed significantly
    if (currentBoundsRef.current === boundsKey) return;
    currentBoundsRef.current = boundsKey;

    setIsLoading(true);
    setError(null);
    try {
      // Calculate how many files to load based on zoom level
      const zoom = map.getZoom();
      const limit = zoom < 5 ? 500 : zoom < 8 ? 1000 : zoom < 10 ? 2000 : 5000;

      // Merge searchQuery filters with bounds
      const results = await searchWeatherFiles({
        ...searchQuery,
        bounds: {
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        },
        limit,
        sortBy: 'popularity',
        sortOrder: 'desc',
      });
      setFiles(results);
    } catch (err) {
      console.error('Failed to load weather files for bounds:', err);
      setError('Failed to load weather stations');
    } finally {
      setIsLoading(false);
    }
  }, [map, searchQuery, searchQueryKey]);

  // Debounce the loading
  const debouncedLoad = useCallback(() => {
    if (loadingTimeoutRef.current) {
      window.clearTimeout(loadingTimeoutRef.current);
    }
    loadingTimeoutRef.current = window.setTimeout(() => {
      loadFilesForBounds();
    }, 300);
  }, [loadFilesForBounds]);

  // Handle map events
  useMapEvents({
    moveend: debouncedLoad,
    zoomend: debouncedLoad,
  });

  // Wait for map to be ready before initial load
  useEffect(() => {
    const handleMapReady = () => {
      isMapReady.current = true;
      loadFilesForBounds();
    };

    // Check if map is already ready
    if (map.getContainer()) {
      // Use whenReady to ensure tiles are loaded
      map.whenReady(handleMapReady);
    }

    return () => {
      if (loadingTimeoutRef.current) {
        window.clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [map, loadFilesForBounds]);

  // Reload when searchQuery changes
  useEffect(() => {
    if (isMapReady.current) {
      // Reset bounds key to force reload
      currentBoundsRef.current = null;
      loadFilesForBounds();
    }
  }, [searchQueryKey, loadFilesForBounds]);

  // Initialize marker cluster group
  useEffect(() => {
    if (!markersRef.current) {
      markersRef.current = L.markerClusterGroup({
        chunkedLoading: true,
        maxClusterRadius: 50,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        disableClusteringAtZoom: 13,
        iconCreateFunction: (cluster) => {
          const count = cluster.getChildCount();
          let size = 'small';
          let sizeLabel = 'small cluster';
          if (count >= 100) {
            size = 'large';
            sizeLabel = 'large cluster';
          } else if (count >= 10) {
            size = 'medium';
            sizeLabel = 'medium cluster';
          }

          return L.divIcon({
            html: `<div class="cluster-marker cluster-${size}" role="button" aria-label="${count} weather stations, ${sizeLabel}" tabindex="0"><span aria-hidden="true">${count}</span></div>`,
            className: 'weather-cluster-icon',
            iconSize: L.point(40, 40),
          });
        },
      });
      map.addLayer(markersRef.current);
    }

    return () => {
      if (markersRef.current) {
        map.removeLayer(markersRef.current);
        markersRef.current = null;
      }
    };
  }, [map]);

  // Update markers when files change
  useEffect(() => {
    if (!markersRef.current) return;

    markersRef.current.clearLayers();

    const markers = files.map((file) => {
      const isSelected = selectedFile?.id === file.id;
      const marker = L.marker([file.location.latitude, file.location.longitude], {
        icon: isSelected ? selectedStationIcon : weatherStationIcon,
      });

      // Create popup content with proper accessibility
      const popupContent = document.createElement('div');
      popupContent.className = 'weather-popup';
      popupContent.setAttribute('role', 'dialog');
      popupContent.setAttribute('aria-label', `Weather station: ${file.location.city}`);

      // Header
      const header = document.createElement('div');
      header.className = 'popup-header';
      const title = document.createElement('strong');
      title.textContent = file.location.city;
      const region = document.createElement('span');
      region.className = 'popup-region';
      region.textContent = `${file.location.region}, ${file.location.country}`;
      header.appendChild(title);
      header.appendChild(region);
      popupContent.appendChild(header);

      // Details
      const details = document.createElement('div');
      details.className = 'popup-details';
      details.setAttribute('role', 'list');

      const detailItems = [
        { label: 'Climate Zone:', value: file.climateZone.ashrae || 'N/A' },
        { label: 'Elevation:', value: `${file.location.elevation}m` },
        { label: 'Source:', value: file.source.type },
      ];

      detailItems.forEach(({ label, value }) => {
        const item = document.createElement('div');
        item.className = 'popup-detail';
        item.setAttribute('role', 'listitem');
        const labelSpan = document.createElement('span');
        labelSpan.className = 'detail-label';
        labelSpan.textContent = label;
        const valueSpan = document.createElement('span');
        valueSpan.className = 'detail-value';
        valueSpan.textContent = value;
        item.appendChild(labelSpan);
        item.appendChild(valueSpan);
        details.appendChild(item);
      });
      popupContent.appendChild(details);

      // Actions
      const actions = document.createElement('div');
      actions.className = 'popup-actions';

      const previewBtn = document.createElement('button');
      previewBtn.className = 'popup-btn preview-btn';
      previewBtn.textContent = 'Preview';
      previewBtn.setAttribute('type', 'button');
      previewBtn.setAttribute('aria-label', `Preview weather file for ${file.location.city}`);

      const selectBtn = document.createElement('button');
      selectBtn.className = 'popup-btn select-btn';
      selectBtn.textContent = 'Select';
      selectBtn.setAttribute('type', 'button');
      selectBtn.setAttribute('aria-label', `Select weather file for ${file.location.city}`);

      actions.appendChild(previewBtn);
      actions.appendChild(selectBtn);
      popupContent.appendChild(actions);

      // Add event listeners
      previewBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        onPreview?.(file);
      });

      selectBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        onSelect?.(file);
      });

      // Keyboard support for buttons
      previewBtn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPreview?.(file);
        }
      });

      selectBtn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect?.(file);
        }
      });

      marker.bindPopup(popupContent, {
        maxWidth: 280,
        minWidth: 200,
      });

      return marker;
    });

    markersRef.current.addLayers(markers);
  }, [files, selectedFile, onSelect, onPreview]);

  return (
    <>
      {isLoading && (
        <div className="absolute left-1/2 top-4 z-[1000] -translate-x-1/2 rounded-full bg-background/90 px-4 py-2 shadow-lg">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span className="text-sm">Loading weather stations...</span>
          </div>
        </div>
      )}
      {error && (
        <div className="absolute left-1/2 top-4 z-[1000] -translate-x-1/2 rounded-full bg-destructive/90 px-4 py-2 text-destructive-foreground shadow-lg">
          <span className="text-sm">{error}</span>
        </div>
      )}
      <div className="absolute bottom-4 left-4 z-[1000] rounded-lg bg-background/90 px-3 py-2 text-sm shadow-lg">
        {isLoading ? 'Loading...' : `${files.length.toLocaleString()} stations in view`}
      </div>
    </>
  );
}

// Locate me button component
function LocateControl() {
  const map = useMap();
  const [isLocating, setIsLocating] = useState(false);

  const handleLocate = useCallback(() => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        map.flyTo([position.coords.latitude, position.coords.longitude], 10, {
          duration: 1.5,
        });
        setIsLocating(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        setIsLocating(false);
        alert('Could not get your location');
      },
      { timeout: 10000 }
    );
  }, [map]);

  return (
    <div className="leaflet-top leaflet-right">
      <div className="leaflet-control leaflet-bar">
        <button
          onClick={handleLocate}
          className="flex h-[30px] w-[30px] items-center justify-center bg-white text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
          title="Go to my location"
          aria-label={isLocating ? 'Finding your location...' : 'Go to my location'}
          disabled={isLocating}
          type="button"
        >
          {isLocating ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden="true" />
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M12 2v4" />
              <path d="M12 18v4" />
              <path d="M2 12h4" />
              <path d="M18 12h4" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

export function WeatherFileMap({
  onSelect,
  onPreview,
  selectedFile,
  initialCenter = [39.8283, -98.5795], // Center of USA
  initialZoom = 4,
  searchQuery,
}: WeatherFileMapProps) {
  // Determine if we have active filters that should change the initial view
  const hasActiveFilters = searchQuery && (
    searchQuery.text ||
    searchQuery.countryCode ||
    searchQuery.climateZone ||
    searchQuery.sourceType ||
    searchQuery.yearRange
  );

  // Use world view when filters are active, otherwise default to USA
  const effectiveCenter = hasActiveFilters ? [20, 0] as [number, number] : initialCenter;
  const effectiveZoom = hasActiveFilters ? 2 : initialZoom;

  // Memoize the map key to prevent unnecessary re-renders
  const mapKey = useMemo(
    () => `map-${effectiveCenter.join('-')}-${effectiveZoom}`,
    [effectiveCenter, effectiveZoom]
  );

  return (
    <div className="relative h-full w-full">
      <style>{`
        .weather-station-marker {
          background: transparent;
        }
        .weather-marker-inner {
          width: 12px;
          height: 12px;
          background: hsl(var(--primary));
          border: 2px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }
        .weather-marker-inner.selected {
          width: 16px;
          height: 16px;
          background: hsl(var(--destructive));
          border-width: 3px;
        }
        .weather-cluster-icon {
          background: transparent;
        }
        .cluster-marker {
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          color: white;
          font-weight: 600;
          font-size: 12px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
        .cluster-small {
          width: 30px;
          height: 30px;
          background: hsl(var(--primary) / 0.85);
          /* Pattern for color-blind accessibility */
          border: 2px solid transparent;
        }
        .cluster-medium {
          width: 36px;
          height: 36px;
          background: hsl(var(--primary) / 0.9);
          /* Dashed border pattern for medium clusters */
          border: 2px dashed rgba(255,255,255,0.5);
        }
        .cluster-large {
          width: 42px;
          height: 42px;
          background: hsl(var(--primary));
          /* Double border pattern for large clusters */
          border: 3px double rgba(255,255,255,0.7);
        }
        .cluster-marker:focus {
          outline: 2px solid hsl(var(--ring));
          outline-offset: 2px;
        }
        .weather-popup {
          font-family: inherit;
        }
        .popup-header {
          margin-bottom: 8px;
        }
        .popup-header strong {
          display: block;
          font-size: 14px;
          color: hsl(var(--foreground));
        }
        .popup-region {
          font-size: 12px;
          color: hsl(var(--muted-foreground));
        }
        .popup-details {
          display: flex;
          flex-direction: column;
          gap: 4px;
          margin-bottom: 10px;
          font-size: 12px;
        }
        .popup-detail {
          display: flex;
          justify-content: space-between;
        }
        .detail-label {
          color: hsl(var(--muted-foreground));
        }
        .detail-value {
          font-weight: 500;
        }
        .popup-actions {
          display: flex;
          gap: 8px;
        }
        .popup-btn {
          flex: 1;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.15s;
        }
        .preview-btn {
          background: hsl(var(--muted));
          color: hsl(var(--foreground));
          border: 1px solid hsl(var(--border));
        }
        .preview-btn:hover {
          background: hsl(var(--muted) / 0.8);
        }
        .select-btn {
          background: hsl(var(--primary));
          color: hsl(var(--primary-foreground));
          border: none;
        }
        .select-btn:hover {
          opacity: 0.9;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 8px;
        }
        .leaflet-popup-content {
          margin: 12px;
        }
        /* Dark mode support */
        .dark .leaflet-tile {
          filter: brightness(0.8) invert(1) contrast(0.9) hue-rotate(180deg);
        }
        .dark .cluster-marker {
          box-shadow: 0 2px 6px rgba(0,0,0,0.5);
        }
      `}</style>
      <MapContainer
        key={mapKey}
        center={effectiveCenter}
        zoom={effectiveZoom}
        className="h-full w-full"
        scrollWheelZoom={true}
        doubleClickZoom={true}
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapContent
          onSelect={onSelect}
          onPreview={onPreview}
          selectedFile={selectedFile}
          searchQuery={searchQuery}
        />
        <LocateControl />
      </MapContainer>
    </div>
  );
}
